<template>
  <div class="form-check"
       :class="[{disabled}, props.class]"
       ref="rootRef"
  >
    <input v-if="showInput"
           type="hidden"
           :name="parsedName"
           :value="inputValue ? trueValue : falseValue"
    />

    <input class="form-check-input"
           type="checkbox"
           :disabled="disabled"
           :checked="inputValue"
           :value="inputValue ? trueValue : falseValue"
           @input="onChange"
           tabindex="0"
           @focus="onFocus"
    >

    <span v-if="deselectIsVisible"
          class="deselect"
          @click="deselectAll"
    />

    <template v-if="!labelDisabled" >
      <slot name="label"
            v-bind="{parsedName, parsedLabel, inputValue, update}"
      >
        <label
            class="form-check-label ms-2 cursor-pointer"
            :for="parsedName"
            @click="update(!inputValue)"
        >
          {{ parsedLabel }}

          <FormInputExplanation
              v-if="explanation"
              :explanation="explanation"
          />
        </label>
      </slot>
    </template>
  </div>
</template>
<script setup>
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import {useFormControl, useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import {computed, nextTick, ref, onUnmounted, watch} from "vue";
import {hideAllPoppers} from "floating-vue";
import FormInputExplanation from "@/js/Elements/Form/FormInputExplanation.vue";

const props = defineProps({
  ...FormControlProps,
  trueValue: {
    type: [String, Number, Boolean],
    default: 1
  },
  falseValue: {
    type: [String, Number, Boolean],
    default: 0
  },
  items: Array,
  itemsKey: {
    type: String,
    default: 'selected',
  },
  showDeselect: [Boolean, Number],
  updateItems: {
    type: Boolean,
    default: true
  },
  onlyIfChecked: Boolean,
  hideInput: Boolean,
});

const emit = defineEmits(['update:modelValue', 'change', 'deselect', 'focus', 'check']);
const {
  parsedName,
  parsedLabel,
} = useFormControlParser(props);
let watcher = null;
const rootRef = ref();
const showInput = computed(() => {
  if (props.hideInput) return false;
  if (!props.onlyIfChecked) return props.name;
  return props.name && inputValue.value;
});
const {
  inputValue
} = useFormControl(props, emit);

const selectedItems = computed(() =>
    props.items ? props.items.filter(item => item[props.itemsKey] === 1) : []
);
const deselectIsVisible = computed(() => {
  return props.showDeselect || (props.items && selectedItems.value.length && !inputValue.value);
});

if (props.items) {
  watchItems(true);
}
onUnmounted(unwatch);

function checkItems() {
  if (!props.items) return;
  setValue(selectedItems.value.length && selectedItems.value.length === props.items.length);
}

function onChange(e) {
  if (props.disabled) return;

  update(e.target.checked);
  nextTick(() => {
    emit('check', inputValue.value ? props.trueValue : props.falseValue);
  });
}

function update(value) {
  setValue(value);
  unwatch();
  if (props.updateItems) {
    setItems();
  }
  watchItems();
}

function setItems() {
  if (!props.items) return;
  props.items.forEach(item =>  {
    item[props.itemsKey] = inputValue.value ? 1 : 0;
  });
}

function setValue(value) {
  inputValue.value = value;
  nextTick(() => {
    emit('change', inputValue.value ? props.trueValue : props.falseValue);
  });
}

function deselectAll() {
  setValue(false);
  setItems();
  emit('deselect');
}

function onFocus(e) {
  const parent = rootRef.value.closest('.v-popper__popper');
  if (!parent){
    hideAllPoppers();
  }
  emit('focus',e)
}

function unwatch() {
  if (!watcher) return;
  watcher();
}

function watchItems(immediate = false) {
  watcher = watch(() => props.items, checkItems, { deep: true, immediate });
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.form-check {
  display: inline-flex;
  align-items: center;
  padding: 0;
  margin-bottom: 0;

  &-input:checked {
    background-color: themeColor('primary');
    border-color: themeColor('primary');
  }

  &.disabled {
    .form-check-input:not(:checked) {
      pointer-events: auto;
      background-color: themeColor('neutral-300');
      border-color: themeColor('neutral-400');
      cursor: not-allowed;
    }
  }

  .form-check-input {
    width: $checkbox-size;
    height: $checkbox-size;
    margin-left: 0;
    margin-top: 0;
  }

  .form-check-label {
    font-size: fontSize(5);
    font-weight: $font-weight-medium;
  }

  &:hover {
    .deselect {
      width: $checkbox-size;
      height: $checkbox-size;
      margin: 0 0 0 spacing(1);
      border-width: toRem(1);
    }
  }
}

.deselect {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: themeColor('border');
  cursor: pointer;
  border-radius: $border-radius;
  transition: width $transition-timing, margin-right $transition-timing;
  width: 0;
  height: 0;
  border-color: themeColor('border');
  border-width: 0;
  border-style: solid;
  overflow: hidden;

  &:before {
    content: '';
    width: toRem(5);
    height: toRem(2);
    background-color: themeColor('neutral-400');
  }
}
</style>